import type { User } from './types'

export const OPERATIONAL_SYSTEM_ROLES = ['SUPER_ADMIN', 'ADMIN'] as const

export type OperationalSystemRole = (typeof OPERATIONAL_SYSTEM_ROLES)[number]

const OPERATIONAL_SYSTEM_ROLE_SET = new Set<string>(OPERATIONAL_SYSTEM_ROLES)

const OPERATIONAL_SYSTEM_ROLE_PRIORITY: Record<OperationalSystemRole, number> = {
  SUPER_ADMIN: 2,
  ADMIN: 1,
}

export const normalizeOperationalSystemRoles = (
  roles: readonly string[] | undefined,
  systemRole?: string
): OperationalSystemRole[] => {
  const normalized = new Set<OperationalSystemRole>()
  const candidates = [...(roles ?? []), ...(systemRole ? [systemRole] : [])]

  for (const candidate of candidates) {
    const normalizedCandidate = candidate.trim().toUpperCase()
    if (!OPERATIONAL_SYSTEM_ROLE_SET.has(normalizedCandidate)) {
      continue
    }

    normalized.add(normalizedCandidate as OperationalSystemRole)
  }

  return Array.from(normalized).sort(
    (left, right) =>
      OPERATIONAL_SYSTEM_ROLE_PRIORITY[right] - OPERATIONAL_SYSTEM_ROLE_PRIORITY[left]
  )
}

export const getUserOperationalSystemRoles = (
  user: User | null | undefined
): OperationalSystemRole[] =>
  normalizeOperationalSystemRoles(user?.systemRoles, user?.systemRole ?? user?.role)

export const hasOperationalSystemRole = (
  user: User | null | undefined,
  requiredRole: OperationalSystemRole
): boolean => {
  const roles = getUserOperationalSystemRoles(user)

  if (requiredRole === 'ADMIN') {
    return roles.includes('ADMIN') || roles.includes('SUPER_ADMIN')
  }

  return roles.includes(requiredRole)
}

export const hasAdminAccess = (user: User | null | undefined): boolean =>
  hasOperationalSystemRole(user, 'ADMIN')

export const hasSuperAdminAccess = (user: User | null | undefined): boolean =>
  hasOperationalSystemRole(user, 'SUPER_ADMIN')
